import React, { useState, useEffect } from 'react';
import { Col, List, Row, Empty, message, Checkbox } from 'antd';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { PaginationBar } from '../entity/shared/tabs/Glossary/PaginationBar';
import ConfirmationModal from '../shared/modals/ConfirmationModal';
import ReadOnlyWarningModal from '../shared/modals/ReadOnlyWarningModal';
import { BulkAction } from '../shared/bulkAction/BulkAction';
import BrowseResultActions from './BrowseResultActions';
import { Notification } from '../shared/Notification';
import analytics, { EventType } from '../analytics';
import BrowseResultCard from './BrowseResultCard';

import { BrowseResultGroup, EntityType, Entity, EntityImportValidationMetadataInput } from '../../types.generated';
import { useExportEntityMutation } from '../../graphql/mutations.generated';
import { useEntityRegistry } from '../useEntityRegistry';
import {
    useBatchDeleteDatasetsMutation,
    useGetEntityImportValidationMetadataQuery,
} from '../../graphql/dataset.generated';

import { EntityXlsxExpectedSheetHeader } from '../entity/shared/types';
import { useTranslation } from '../../i18n/useTranslation';
import utilService from '../../utils/util.service';
import { useRbac } from '../entity/shared/useRbac';
import { BULK_IMPORT_PLATFORM_PRIVILEGE_MAP, ENTITY_ACTIONS } from '../entity/shared/rbacConstantsService';

const EntityList = styled(List)`
    .ant-list-item {
        padding: 12px;
    }
`;

interface Props {
    type: EntityType;
    rootPath: string;
    page: number;
    pageSize: number;
    totalResults: number;
    groups: Array<BrowseResultGroup>;
    entities: Array<Entity>;
    onChangePage: (page: number) => void;
}

type SelectedItemsAccumulator = {
    urns: string[];
    paths: string[];
};

const input: EntityImportValidationMetadataInput = {
    entityType: 'dataset',
};

/**
 * Display browse groups + entities.
 */
export const BrowseResults = ({
    type,
    rootPath,
    page,
    pageSize,
    totalResults,
    entities,
    groups,
    onChangePage,
}: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const history = useHistory();
    const { canPerformEntityMenuAction, platformPrivileges } = useRbac();

    const getPrivileges = (action) => {
        return canPerformEntityMenuAction(type, action);
    };

    const isDatasetType = type === EntityType.Dataset;
    const canBulkImport = !isDatasetType || platformPrivileges?.[BULK_IMPORT_PLATFORM_PRIVILEGE_MAP[type]];
    const canExport = !isDatasetType || getPrivileges(ENTITY_ACTIONS.EXPORT);
    const canDelete = !isDatasetType || getPrivileges(ENTITY_ACTIONS.DELETE);

    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const [datasetUrns, setDatasetUrns] = useState<string[]>([]);
    const [chosenPaths, setChosenPaths] = useState<string[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
    const [readOnlyDatasetNames, setReadOnlyDatasetNames] = useState<string[]>([]);
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [exportEntity, setExportEntity]: any = useState(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [batchDeleteDatasetsMutation] = useBatchDeleteDatasetsMutation();
    const [exportEntityMutation] = useExportEntityMutation();

    useEffect(() => {
        // Reset selected items and "Select All" when the browse path changes
        setIsAllSelected(false);
        setDatasetUrns([]);
        setChosenPaths([]);
    }, [rootPath]);

    const { data: headers } = useGetEntityImportValidationMetadataQuery({ variables: { input } });

    const onGroupClick = (group: BrowseResultGroup) => {
        analytics.event({
            type: EventType.BrowseResultClickEvent,
            browsePath: rootPath,
            entityType: type,
            resultType: 'Group',
            groupName: group.originalName,
        });
    };

    const onEntityClick = (entity: Entity) => {
        analytics.event({
            type: EventType.BrowseResultClickEvent,
            browsePath: rootPath,
            entityType: type,
            resultType: 'Entity',
            entityUrn: entity.urn,
        });
    };

    /**
     * Handles the click event on the select all checkbox.
     * Updates the selectedItems state to include all datasets name if the select all checkbox is checked,
     * or clears the selectedItems state if the select all checkbox is unchecked.
     *
     *  * @param event - The click event object.
     */
    const handleSelectAll = (e) => {
        const dsNames: any = [...groups, ...entities];
        const selectedDsNames = e.target.checked ? dsNames : [];

        const { urns, paths } = selectedDsNames.reduce(
            (acc: SelectedItemsAccumulator, item: any) => {
                if (item?.urn) {
                    acc.urns.push(item.urn);
                } else {
                    acc.paths.push(item.name);
                }
                return acc;
            },
            { urns: [], paths: [] },
        );

        setDatasetUrns(urns);
        setChosenPaths(paths);
        setIsAllSelected(e.target.checked);
    };

    // Handles the deletion of a dataset.
    const handleDelete = () => {
        setIsLoading(true);
        setIsModalVisible(true);

        try {
            const variables = {
                input: {
                    currentPath: utilService.decodeSpecialChars(rootPath?.split('/dataset')[1]),
                    chosenPaths,
                    datasetUrns,
                },
            };
            // Mutation to delete multiple datasets in a batch operation
            batchDeleteDatasetsMutation({ variables })
                .then(() => {
                    message.success({ content: t('Initiating Dataset Deletion Process'), duration: 3 });
                    setChosenPaths([]);
                    setDatasetUrns([]);
                    setIsAllSelected(false);
                    history.push('/browse/dataset');
                })
                .catch(() => message.error({ content: t('Failed to delete the datasets'), duration: 3 }));

            setIsLoading(false);
            setIsModalVisible(false);
        } catch (error) {
            message.error({ content: t('Failed to delete the datasets'), duration: 3 });
        }
    };

    // Handles the click event on the checkbox for groups.
    const handleItemCheck = (dsName: string, event: React.MouseEvent) => {
        event.stopPropagation();

        const isSelected = chosenPaths.includes(dsName);
        const updatedPaths = isSelected ? chosenPaths.filter((name) => name !== dsName) : [...chosenPaths, dsName];

        setChosenPaths(updatedPaths);

        // Update isAllSelected based on total selected count
        const totalSelected = updatedPaths.length + datasetUrns.length;
        const totalSelectable = groups.length + entities.length;
        setIsAllSelected(totalSelected === totalSelectable && totalSelectable > 0);
    };

    // Checks if the type is 'DATASET'.
    const isDataset = () => type === 'DATASET';

    // Handles the deletion of a dataset from the preview page.
    const deleteDatasetFromPreview = (dataset, e) => {
        e.stopPropagation();

        const dsURN = dataset?.urn;
        const isSelected = datasetUrns.includes(dsURN);
        const updatedDsUrns = isSelected ? datasetUrns.filter((urn) => urn !== dsURN) : [...datasetUrns, dsURN];

        setDatasetUrns(updatedDsUrns);

        // Update isAllSelected based on total selected count
        const totalSelected = chosenPaths.length + updatedDsUrns.length;
        const totalSelectable = groups.length + entities.length;
        setIsAllSelected(totalSelected === totalSelectable && totalSelectable > 0);
    };

    /**
     * Handles the export of the selected datasets to a CSV file.
     * Calls the exportEntityMutation to export the selected datasets to a CSV file.
     * Displays a notification message to the user once the export is completed.
     * Clears the selectedItems state and sets the isAllSelected state to false.
     */
    const exportCsv = () => {
        setIsCsvExporting(true);
        setExportEntity(null);

        const exportInput = {
            dataset: {
                currentPath: utilService.decodeSpecialChars(rootPath?.split('/dataset')[1]),
                chosenPaths,
                datasetUrns,
            },
            entityType: 'dataset',
        };

        exportEntityMutation({
            variables: {
                input: exportInput,
            },
        })
            .then((res = {}) => {
                const { data } = res;
                setExportEntity(data?.exportEntity);
                setChosenPaths([]);
                setDatasetUrns([]);
                setIsAllSelected(false);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...exportInput,
                        async: true,
                    };

                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setChosenPaths([]);
                        setDatasetUrns([]);
                        setIsAllSelected(false);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    // Get list of read-only dataset names from selected items
    const getReadOnlyDatasetNames = () => {
        const allItems: any = [...groups, ...entities];
        const selectedItemsUrn = [...chosenPaths, ...datasetUrns];
        const selectedReadOnlyItems = allItems.filter(
            (item) => selectedItemsUrn.includes(item?.urn || item?.name) && item?.readOnly?.readOnly === true,
        );
        return selectedReadOnlyItems.map((item) => item?.name).filter(Boolean);
    };

    // Displays the confirmation modal dialog when the user clicks the "Delete Datasets" button.
    const confirmDatasetDeletion = () => {
        const readOnlyNames = getReadOnlyDatasetNames();
        if (readOnlyNames.length > 0) {
            setReadOnlyDatasetNames(readOnlyNames);
            setIsWarningModalVisible(true);
        } else {
            setIsModalVisible(true);
        }
    };

    // Closes the confirmation modal dialog.
    const onClose = () => {
        setIsModalVisible(false);
    };

    // Closes the warning modal dialog.
    const onWarningModalClose = () => {
        setIsWarningModalVisible(false);
    };

    // Calculate indeterminate state for select all checkbox
    const isIndeterminate = () => {
        const selectableCount = ([...groups, ...entities] as any[]).length;
        const selectedCount = chosenPaths.length + datasetUrns.length;
        return selectedCount > 0 && selectedCount < selectableCount;
    };

    return (
        <div>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            <Row>
                <Col span={24}>
                    <BrowseResultActions
                        selectedItems={[...chosenPaths, ...datasetUrns]}
                        isAllSelected={isAllSelected}
                        isIndeterminate={isIndeterminate()}
                        handleSelectAll={handleSelectAll}
                        confirmDatasetDeletion={confirmDatasetDeletion}
                        exportCsv={exportCsv}
                        isCsvExporting={isCsvExporting}
                        expectedHeaders={
                            (headers?.getEntityImportValidationMetadata?.validationMetadata || []).map((header) => ({
                                ...header,
                                sampleRows: header.sampleRows.map((row) => row.map((cell) => cell || '')),
                            })) as EntityXlsxExpectedSheetHeader[]
                        }
                        showCheckbox={isDataset()}
                        canBulkImport={canBulkImport}
                        canExport={canExport}
                        canDelete={canDelete}
                        unauthorisedTooltip={t('UNAUTHORISED_TOOLTIP')}
                    />
                </Col>
                {groups.map((group) => (
                    <Col span={24} key={`${group.name}_key`}>
                        <BrowseResultCard
                            canDelete={canDelete}
                            canExport={canExport}
                            unauthorisedTooltip={t('UNAUTHORISED_TOOLTIP')}
                            onClick={() => onGroupClick(group)}
                            name={group.name}
                            count={group.count}
                            url={`${rootPath}/${utilService.encodeSpecialChars(group.originalName)}`}
                            type={entityRegistry.getCollectionName(type)}
                            selectedItems={chosenPaths}
                            handleItemCheck={handleItemCheck}
                            showCheckbox={isDataset()}
                        />
                    </Col>
                ))}
                {(!(groups && groups.length > 0) || (entities && entities.length > 0)) && (
                    <EntityList
                        split={false}
                        dataSource={entities}
                        className="f-list-with-divider"
                        renderItem={(item: any) => {
                            return (
                                <Row>
                                    {isDataset() && (
                                        <Checkbox
                                            aria-label={item?.name}
                                            style={{ paddingLeft: 12 }}
                                            checked={datasetUrns.includes(item?.urn)}
                                            onClick={(e) => {
                                                deleteDatasetFromPreview(item, e);
                                            }}
                                        />
                                    )}
                                    <List.Item style={{ flex: 1 }} onClick={() => onEntityClick(item as Entity)}>
                                        {entityRegistry.renderBrowse(type, item)}
                                    </List.Item>
                                </Row>
                            );
                        }}
                        locale={{
                            emptyText: <Empty description={t('No Entities')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                        }}
                    />
                )}
                <Col span={24}>
                    <PaginationBar
                        page={page}
                        pageSize={pageSize}
                        loading={isLoading}
                        pageSizeOptions={[]}
                        totalCount={totalResults}
                        onChangePage={onChangePage}
                        onShowSizeChange={() => {}}
                    />
                </Col>
            </Row>

            <BulkAction
                type="dataset"
                selectedItems={[...chosenPaths, ...datasetUrns]}
                exportCsv={exportCsv}
                confirmDatasetDeletion={confirmDatasetDeletion}
                clearSelectedItems={() => {
                    setChosenPaths([]);
                    setDatasetUrns([]);
                    setIsAllSelected(false);
                }}
                canExport={canExport}
                canDelete={canDelete}
                unauthorisedTooltip={t('UNAUTHORISED_TOOLTIP')}
            />

            <ReadOnlyWarningModal
                visible={isWarningModalVisible}
                onClose={onWarningModalClose}
                readOnlyItems={readOnlyDatasetNames}
                entityType="Datasets"
            />

            <ConfirmationModal
                title={t('Delete Dataset')}
                okText={t('Delete')}
                cancelText={t('Cancel')}
                isLoading={isLoading}
                onClick={handleDelete}
                visible={isModalVisible}
                onClose={onClose}
                confirmationText={t('DELETE_DATASET_CONFIRMATION_TEXT')}
            />
        </div>
    );
};
